import { gql } from "@apollo/client"

export const ENABLE_DISABLE_KOL_PROFILE = gql`
  mutation approveKolProfile($kolProfileId: uuid!, $isApproved: Boolean, $isActive: Boolean) {
    update_kol_profile_by_pk(pk_columns: { id: $kolProfileId }, _set: { is_active: $isActive, is_approved: $isApproved }) {
      id
      is_active
      is_approved
    }
  }
`

export const CHECK_EXISTING_KOL_EMAILS = gql`
  query CheckExistingKolEmails($emails: [String!]!) {
    kol_profile(where: { email: { _in: $emails } }) {
      email
    }
  }
`

export const BULK_INSERT_KOLS = gql`
  mutation BulkInsertKols($input: [BulkInsertKolsInput!]!) {
    bulkInsertKols(input: $input) {
      success
      message
    }
  }
`

// Manually trigger the (deferred) bulk-upload invitation email for one or more
// KOLs. Backend only emails profiles that are still invited (is_invited = true)
// and have an email. Output: { success, message }.
export const SEND_BULK_INSERT_KOLS_SUCCESS_EMAIL = gql`
  mutation SendBulkInsertKolsSuccessEmail($kol_profile_ids: [uuid!]!) {
    sendBulkInsertKolsSuccessEmail(kol_profile_ids: $kol_profile_ids) {
      success
      message
    }
  }
`

export const GET_KOL_USER_ID_BY_KOL_PROFILE_ID = gql`
  query GetKOL($id: uuid!) {
    kol_profile_by_pk(id: $id) {
      user_id
    }
  }
`

export const GET_KOL_USER_BY_ID = gql`
  query GetUser($id: uuid!) {
    user_by_pk(id: $id) {
      email
      authUserId
      last_login
      created_at
      delete_reason
    }
  }
`

export const GET_KOL_AVERAGE_RATING = gql`
  query GetKolRating($ids: [uuid!]) {
    kol_ratings_stats(where: { kol_profile_id: { _in: $ids } }) {
      average_rating
      kol_profile_id
    }
  }
`

export const GET_KOL_RATING_FULL_INFORMATION = gql`
  query GetKolRatingFullInformation($id: uuid!) {
    kol_profile(where: { id: { _eq: $id } }) {
      first_name
      last_name
      kol_file_metadata(order_by: { created_at: desc }) {
        type
        created_at
        file_metadata {
          url
        }
      }
    }
    kol_ratings_stats(where: { kol_profile_id: { _eq: $id } }) {
      kol_profile_id
      average_rating
      total_ratings
      content_performance
      punctuality
      content_quality_and_creativity
      professionalism_and_responsiveness
    }
    kol_ratings(where: { kol_profile_id: { _eq: $id } }) {
      content_performance
      punctuality
      content_quality_and_creativity
      professionalism_and_responsiveness
      comment
      client_profile {
        company_name
      }
      campaign {
        name
      }
    }
  }
`

export const GET_UNLINK_REQUESTS = gql`
  query GetUnlinkRequests($kol_profile_id: uuid!) {
    kol_unlink_request(where: { kol_profile_id: { _eq: $kol_profile_id } }) {
      status
      social_platform_code
      reason
      platform_username
      created_at
      id
    }
  }
`

export const GET_ACCOUNT_VERIFICATION_REQUEST = gql`
  query GetAccountVerificationRequest($kol_profile_id: uuid!) {
    account_verification_requests(where: { kol_profile_id: { _eq: $kol_profile_id } }) {
      id
      kol_profile_id
      identifier
      follower_count
      type
      ip_check
      country
      status
      profile_url
      created_at
    }
  }
`

export const UPDATE_ACCOUNT_VERIFICATION_REQUEST = gql`
  mutation UpdateAccountVerificationRequest($id: uuid!, $status: String!) {
    update_account_verification_requests_by_pk(pk_columns: { id: $id }, _set: { status: $status }) {
      id

      status
    }
  }
`

export const INSERT_UNLINK_REQUEST = gql`
  mutation InsertUnlinkRequest(
    $kol_profile_id: uuid!
    $social_platform_code: String!
    $reason: String!
    $platform_username: String!
    $status: String!
  ) {
    insert_kol_unlink_request(
      objects: [
        {
          kol_profile_id: $kol_profile_id
          social_platform_code: $social_platform_code
          reason: $reason
          platform_username: $platform_username
          status: $status
        }
      ]
    ) {
      affected_rows
    }
  }
`

export const UPDATE_KOL_UNLINK_REQUEST = gql`
  mutation UpdateKolUnlinkRequest($id: uuid!, $status: String!) {
    update_kol_unlink_request(where: { id: { _eq: $id } }, _set: { status: $status }) {
      returning {
        created_at
        id
        kol_profile_id
        platform_username
        reason
        social_platform_code
        status
      }
      affected_rows
    }
  }
`

export const GET_KOL_PROFILE_SHARED_TOKEN = gql`
  query GetKolProfileSharedToken($id: uuid!) {
    kol_profile_by_pk(id: $id) {
      shared_token
    }
  }
`

export const GET_CAMPAIGN_ID_BY_SHARED_TOKEN = gql`
  query getCampaignDataBySharedToken($sharedToken: String!) {
    getCampaignDataBySharedToken(sharedToken: $sharedToken) {
      campaign_id
    }
  }
`

export const GET_CAMPAIGN_BY_ID = gql`
  query GetCampaignById($id: uuid!) {
    campaign_by_pk(id: $id) {
      name
    }
  }
`

export const GET_KOL_IC_PHOTOS = gql`
  query GetKolIcPhotos($kol_profile_id: uuid!) {
    kol_file_metadata(where: { kol_profile_id: { _eq: $kol_profile_id }, _and: { type: { _eq: "ic" } } }) {
      file_metadata {
        id
        url
      }
    }
  }
`

export const GET_KOL_SSM_PHOTOS = gql`
  query GetKolSsmPhotos($kol_profile_id: uuid!) {
    kol_file_metadata(where: { kol_profile_id: { _eq: $kol_profile_id }, _and: { type: { _eq: "ssm" } } }) {
      file_metadata {
        id
        url
      }
    }
  }
`

export const GET_KOL_PROFILES_BY_TALENT_ID = gql`
  query GetKolProfilesByTalentId($talent_id: uuid!) {
    kol_profile(where: { talent_manager_id: { _eq: $talent_id } }) {
      id
      first_name
      last_name
      social_platforms {
        id
        social_platform_code
        platform_user_id
        platform_username
      }
    }
  }
`
